import React, {useState, useEffect} from 'react'
import {useSelector} from 'react-redux'
import {selectSelectedOptionId} from '../../../../app-store/ProposalSlice'
import {buildCaptureOrderPayload} from './build-capture-order-payload'
import {
  Container,
  FooterText,
  GradientSection,
  NavBar,
  NavBusinessName,
  NavForLabel,
  NavLeft,
  NavRight,
  PageFooter,
  PageWrap,
} from '../ClientProposalView/styled.components'
import {PTKeapWhite, PTLogoWhite} from '../../../../assets'

import {useEnsureProposalInfo} from '../../../../utils/client-proposal.util'
import {validateEmailWithApi} from '../../../../accountManagement/features/EmailLookup/EmailLookup.utils'
import {useProposalAuthClient} from './contexts/proposal-auth-context'

import {useNavigate, useParams} from 'react-router'
import {Spinner} from '../../../../components/loader-spinner'
import {HeaderTitle} from '../../../../components/header-title'
import {PaymentAuthProvider} from '../../../../components/payment-information-components/contexts/payment-auth-context'
import {PaymentInformationProvider} from '../../../../components/payment-information-components/contexts/payment-information-provider'
import {ToastContainer} from 'react-toastify'
import {PaymentInformationGate} from '../../../../components/payment-information-components/payment-information-gate'
import {
  buildSalesAttributionFields,
  buildSendlinkSubscriptionGroups,
} from '../../../../features/CatalogGrid/utils/buildSendLinkSubscriptionGroups'

// Tags each subscription/fee item with the resolved thryvId — mirrors
// tagLocation in core OOE's buildSendLinkPayload.js, whose output the
// same downstream consumers (payment-information.js, cc-desktop.js) key
// off of via item.locationThryvId.
function tagLocation(items, locationThryvId) {
  return items.map(item => ({...item, locationThryvId}))
}

// Mirrors extractLocationDetail in core OOE's buildSendLinkPayload.js. This
// flow only ever has one location today (thryvId), but locationDetails is
// built as an array so multi-location proposals can add more entries later
// without changing the payload shape order_location_detail sync expects.
function extractLocationDetail(
  thryvId,
  subscriptionCustomFields,
  proposalInfo,
) {
  return {
    locationThryvId: thryvId,
    mcDomain: subscriptionCustomFields.mcDomain,
    keapDomain: subscriptionCustomFields.keapDomain,
    keapTrailId: subscriptionCustomFields.keapTrailId,
    tlDomain: subscriptionCustomFields.tlDomain,
    seoProposalId: subscriptionCustomFields.seoProposalId,
    seoDomain: subscriptionCustomFields.seoDomain,
    thryvLeadsId: subscriptionCustomFields.thryvLeadsId,
    cohortEaid: subscriptionCustomFields.cohortEaid,
    isCohort: subscriptionCustomFields.isCohort,
    onboardingApptId: proposalInfo.apptId,
  }
}

export const ProposalPaymentView = () => {
  const client = useProposalAuthClient()
  const {randomId} = useParams()
  const {proposalInfo} = useEnsureProposalInfo({
    proposalId: randomId,
    client,
  })

  const navigate = useNavigate()
  const selectedOptionId = useSelector(selectSelectedOptionId)
  const [isLoading, setIsLoading] = useState(true)
  const [isErrorTriggered, setErrorTriggerStatus] = useState(false)

  /**
   * Builds the account info fields for the cc/order/send-link payload:
   * name, email, company, currency, mailing address, phone, and (AU/NZ
   * only) ABN. Recurly account info (from validateEmailWithApi) takes
   * priority over the proposal's own stored fields, since it reflects the
   * client's actual account once one already exists.
   *
   * @param {object} proposalInfo - the proposal's stored client/business info
   * @param {object} recurlyAccountInfo - subDetails from validateEmailWithApi,
   *   or {} if the lookup found no existing account
   * @returns {object} {firstName, lastName, email, company, currency,
   *   businessAddress, contactPhone, abn?} — abn is included only for AU/NZ
   *   proposals that have one
   */
  function buildAccountInfoFields(proposalInfo, recurlyAccountInfo) {
    const addr = recurlyAccountInfo?.address ?? {}
    const firstName =
      recurlyAccountInfo?.firstName || proposalInfo.clientFirstName || ''
    const lastName =
      recurlyAccountInfo?.lastName || proposalInfo.clientLastName || ''

    return {
      firstName,
      lastName,
      email: proposalInfo.clientEmail || recurlyAccountInfo?.email || '',
      company: proposalInfo.businessName ?? '',
      currency: recurlyAccountInfo?.parentSubscriptions?.[0]?.currency || 'USD',
      businessAddress: {
        street: proposalInfo.address || addr.street1 || addr.street || '',
        city: proposalInfo.city || addr.city || '',
        state: proposalInfo.state || addr.region || addr.state || '',
        postalCode: proposalInfo.postCode || addr.zip || addr.postalCode || '',
        country: proposalInfo.country || addr.country || 'US',
        phone: proposalInfo.telephone || addr.phone || '',
      },
      contactPhone: proposalInfo.clientPhone || addr.phone || '',
      ...(['AU', 'NZ'].includes(proposalInfo.country) && proposalInfo.abn
        ? {abn: proposalInfo.abn.replaceAll(' ', '')}
        : {}),
    }
  }

  useEffect(() => {
    const sendPaymentLink = async () => {
      try {
        if (!randomId || !proposalInfo.clientEmail) return

        // If we already generated a payment link for this option in this
        // session, skip the API call and navigate directly to the existing
        // UUID. This prevents re-firing when the user navigates back to
        // this page and also handles re-selection of the same option.
        if (selectedOptionId) {
          const cacheKey = `pt_payment_${randomId}_${selectedOptionId}`
          const cachedUuid = sessionStorage.getItem(cacheKey)
          if (cachedUuid) {
            navigate(`/payment-information/${cachedUuid}?version=cc`, {
              replace: true,
              state: {flowType: 'Proposal'},
            })
            setIsLoading(false)
            return
          }
        }

        // Fetch Recurly account info directly — intentionally NOT dispatching
        // setRecurlyAccountInfo to Redux to avoid re-merging the account's
        // existing subscriptions back into the proposal option items.
        let recurlyAccountInfo = {}
        let accountThryvId = ''
        try {
          const {meta} = await validateEmailWithApi({
            email: proposalInfo.clientEmail,
            client,
            flowType: 'Proposal',
          })
          recurlyAccountInfo = meta?.subDetails ?? {}
          accountThryvId = meta?.thryvId ?? ''
        } catch {
          // Non-fatal: payload falls back to proposalInfo fields below
        }

        const selectedOption =
          proposalInfo.options?.find(
            opt => opt.optionId === selectedOptionId,
          ) ||
          proposalInfo.options?.find(opt => opt.isSelected) ||
          proposalInfo.options?.[0]

        const thryvId = proposalInfo.thryvId || accountThryvId || ''

        // ── Account info (mirrors sendCCPaymentLink in upgrade-order.js) ─────────
        // Returns {firstName, lastName, email, company, currency,
        // businessAddress, contactPhone, abn?} — spread into `data` below.
        const accountInfoFields = buildAccountInfoFields(
          proposalInfo,
          recurlyAccountInfo,
        )

        // ── Subscription groups + send-link payload fields ────────────────
        // Shared with core OOE's send-link flow (buildSendLinkPayload.js),
        // which calls the same buildSendlinkSubscriptionGroups on its own
        // location items.
        //
        // parentSubscriptions/childSubscriptions/oneTimeFees are tagged with
        // thryvId below (mirrors tagLocation in buildSendLinkPayload.js) and
        // set directly on `data`; subscriptionCustomFields (isCohort/
        // keapAccount/keapId/isInvoiceBilled, and any of mcDomain/
        // keapDomain/keapTrailId/tlDomain/seoProposalId/seoDomain/
        // thryvLeadsId/cohortEaid that apply) is spread directly onto `data`
        // (like before) AND passed through as-is to extractLocationDetail
        // below, for locationDetails.
        const {
          parentSubscriptions,
          childSubscriptions,
          oneTimeFees,
          subscriptionCustomFields,
        } = buildSendlinkSubscriptionGroups(selectedOption?.items)
        const data = {
          appUrl: window.location.origin,
          flow: 'new',
          repId: proposalInfo?.salesRepId,
          repRole: proposalInfo?.userRole,
          thryvId,
          sendLinkVia: proposalInfo.communicationMedium ?? 'email',
          isCohort: false,
          keapAccount: false,
          keapId: '',

          ...accountInfoFields,

          parentSubscriptions: tagLocation(parentSubscriptions, thryvId),
          childSubscriptions: tagLocation(childSubscriptions, thryvId),
          oneTimeFees: tagLocation(oneTimeFees, thryvId),
          coupons: (selectedOption?.coupons ?? [])
            .map(c => c.code ?? c.couponCode)
            .filter(Boolean),

          ...subscriptionCustomFields,

          // One entry today (this flow is single-location) — see
          // extractLocationDetail above.
          locationDetails: [
            extractLocationDetail(
              thryvId,
              subscriptionCustomFields,
              proposalInfo,
            ),
          ].filter(d => Boolean(d.locationThryvId)),

          ...buildSalesAttributionFields(proposalInfo),
        }

        if (proposalInfo.apptId) data.onboardingApptId = proposalInfo.apptId

        // Capture order header BEFORE sending payment link so the order_id
        // can be passed as orderHeaderId to cc/order/send-link.
        // Mirrors captureOrder5NewUpgrade('send-link') in new-order.js.
        let capturedOrderId = null
        try {
          const capturePayload = buildCaptureOrderPayload(
            proposalInfo,
            recurlyAccountInfo,
            selectedOption,
            accountThryvId,
          )
          const captureResult = await client(
            'ordercapture/services/createOrder',
            {
              data: capturePayload,
              method: 'POST',
            },
          )
          capturedOrderId = captureResult?.order_id ?? null
        } catch (captureErr) {
          console.error('ordercapture/services/createOrder failed', captureErr)
        }

        if (capturedOrderId) {
          data.orderHeaderId = capturedOrderId
        }

        const sendLinkDetail = await client('cc/order/send-link', {
          data,
          method: 'POST',
          headers: {'app-type': 'client-proposal-link'},
        })

        if (selectedOptionId) {
          sessionStorage.setItem(
            `pt_payment_${randomId}_${selectedOptionId}`,
            sendLinkDetail.paymentUuid,
          )
        }
        navigate(
          `/payment-information/${sendLinkDetail.paymentUuid}?version=cc`,
          {replace: true, state: {flowType: 'Proposal'}},
        )
      } catch (err) {
        console.log(err)
        setErrorTriggerStatus(true)
      } finally {
        setIsLoading(false)
      }
    }
    sendPaymentLink()
  }, [randomId, proposalInfo.clientEmail, selectedOptionId])

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spinner />
      </div>
    )
  }

  return (
    isErrorTriggered && (
      <PageWrap>
        <Container>
          <NavBar>
            <NavLeft>
              <img
                src={PTLogoWhite}
                alt="Thryv"
                style={{height: 30, objectFit: 'contain'}}
              />
              <span />
              <img
                src={PTKeapWhite}
                alt="Keap"
                style={{height: 30, objectFit: 'contain'}}
              />
            </NavLeft>
            {proposalInfo.businessName && (
              <NavRight>
                <NavForLabel>Proposal for</NavForLabel>
                <NavBusinessName>{proposalInfo.businessName}</NavBusinessName>
              </NavRight>
            )}
          </NavBar>

          <GradientSection style={{flex: 1}}>
            <HeaderTitle title="Payment information">
              <PaymentAuthProvider flowType="Proposal">
                <PaymentInformationProvider>
                  <PaymentInformationGate />
                </PaymentInformationProvider>
              </PaymentAuthProvider>
              <ToastContainer />
            </HeaderTitle>
            <PageFooter>
              <FooterText>Copyright &copy; 2026 Thryv, Inc.</FooterText>
            </PageFooter>
          </GradientSection>
        </Container>
      </PageWrap>
    )
  )
}
